import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboard, Settings, ExternalLink, ChevronDown, LogOut } from 'lucide-react';
import { useUser, useClerk } from '@clerk/clerk-react';

const navItems = [
  { path: '/agents', label: 'Agents', icon: LayoutDashboard },
  { path: '/settings', label: 'Settings', icon: Settings },
];

export default function NavBar() {
  const location = useLocation();
  const { user } = useUser();
  const { signOut } = useClerk();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const handleLogout = () => {
    localStorage.removeItem('tenantSlug');
    signOut();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  return (
    <nav className="sticky top-0 z-50 bg-[#09090b]/80 backdrop-blur-xl border-b border-white/[0.04]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-14">
          <NavLink to="/" className="flex items-center gap-2 group">
            <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10" />
              <circle cx="12" cy="12" r="6" />
              <circle cx="12" cy="12" r="2" />
              <path d="M12 2 L12 5 M10 3 L12 1 L14 3" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M22 12 L19 12 M21 10 L23 12 L21 14" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M12 22 L12 19 M10 21 L12 23 L14 21" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M2 12 L5 12 M3 10 L1 12 L3 14" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="font-semibold text-lg tracking-tight text-white">KALIBR</span>
          </NavLink>

          <div className="flex items-center gap-1">
            {navItems.map(({ path, label, icon: Icon }) => {
              const isActive = location.pathname.startsWith(path);
              return (
                <NavLink
                  key={path}
                  to={path}
                  className={`relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive ? 'text-white' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.04]'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                  {isActive && <span className="absolute -bottom-[17px] left-4 right-4 h-0.5 bg-red-500 rounded-full" />}
                </NavLink>
              );
            })}
          </div>

          <div className="flex items-center gap-3">
            <a
              href="https://kalibr.systems/docs"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              Docs
              <ExternalLink className="w-3 h-3" />
            </a>
            {user && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.04] rounded-lg transition-colors"
                >
                  <span className="max-w-[150px] truncate">{user.primaryEmailAddress?.emailAddress || user.emailAddresses[0]?.emailAddress}</span>
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>

                {showDropdown && (
                  <div className="absolute right-0 mt-2 w-56 bg-zinc-900 rounded-lg shadow-xl border border-white/[0.08] py-1 z-50">
                    <div className="px-4 py-2 border-b border-white/[0.08]">
                      <p className="text-xs text-zinc-500">Signed in as</p>
                      <p className="text-sm text-zinc-300 truncate">{user.primaryEmailAddress?.emailAddress || user.emailAddresses[0]?.emailAddress}</p>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-400 hover:bg-white/[0.04] transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
